import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowClockwise, CheckCircle, FileText, PencilSimple, Robot, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const LANG_STATUS_META = {
  exact: ["Exact match", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  modified: ["Modified", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#7c4a03]"],
  missing: ["Missing", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  counsel_review: ["Counsel review", "border-primary/30 bg-accent text-accent-foreground"],
};

const LangBadge = ({ status, testid }) => {
  const [label, cls] = LANG_STATUS_META[status] || LANG_STATUS_META.missing;
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={testid}>{label}</span>;
};

const labelFor = (list, value) => (list.find(([key]) => key === value) || [null, value || "—"])[1];

export const StepNote = ({ step, children, testid }) => (
  <div className="mt-2 rounded-md border border-primary/20 bg-accent/50 px-3 py-2 text-xs leading-relaxed text-stone-600" data-testid={testid}>
    {step && <span className="mr-1.5 inline-block rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">{step}</span>}
    {children}
  </div>
);

const SOURCE_TYPES_SHORT = {
  regulation: "Law / regulation", survey_guidance: "Survey guidance", payer_guidance: "Payer guidance",
  agency_policy: "Agency policy", handbook: "Handbook", orientation: "Orientation",
  training_material: "Training material", letter_workflow: "Letter/workflow", other: "Other",
};

export function ReviewQueueTab({ onOpen, onChanged }) {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/knowledge/review-queue");
      setQueue(data.queue || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (sid, action) => {
    setBusy(`${action}-${sid}`);
    try {
      await api.post(`/knowledge/sources/${sid}/review`, { action });
      toast.success(action === "approved" ? "Source approved into the knowledge base" : "Source archived");
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (queue === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Loading review queue…</div>;

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="review-queue-panel">
      <div className="border-b border-stone-200 p-4">
        <h2 className="font-heading text-xl font-semibold text-stone-900">Source review queue</h2>
        <StepNote step="Step 2 of 5" testid="stepnote-queue">
          Approve means: <strong>&ldquo;this document is real, current, and applies to us.&rdquo;</strong> Approval unlocks AI analysis, mappings, and reports — it changes nothing for staff. Archive = outdated or junk. One wrong line inside a document does <strong>not</strong> block approval — you handle that later per-requirement (mark it missing/counsel or delete just that line). Not all-or-nothing.
        </StepNote>
        <p className="mt-1 text-xs text-stone-500">
          {queue.length === 0
            ? "All sources reviewed. Approved sources now power search, gap reports, and policy suggestions."
            : `${queue.length} source(s) awaiting review — in recommended order (exact-wording extract first, then regulations, protective services, EVV, TB, payer, survey POCs). Open a source to read it, then approve or archive.`}
        </p>
      </div>
      <div className="divide-y divide-stone-100">
        {queue.map((s, i) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`queue-item-${s.id}`}>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-100 text-xs font-bold text-stone-500">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-stone-900">{s.title}</p>
              <p className="text-xs text-stone-400">
                {SOURCE_TYPES_SHORT[s.source_type] || s.source_type} · {s.extraction_status === "extracted" ? `${s.chunk_count} chunks` : "OCR needed"}
                {s.effective_date ? ` · effective ${String(s.effective_date).slice(0, 10)}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => onOpen(s.id)}
                className="rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-accent" data-testid={`queue-open-${s.id}`}>
                Review content
              </button>
              <button onClick={() => act(s.id, "approved")} disabled={busy === `approved-${s.id}`}
                className="inline-flex items-center gap-1 rounded-md border border-[#27AE60]/40 px-2.5 py-1.5 text-xs font-medium text-[#1e8449] hover:bg-[#27AE60]/10" data-testid={`queue-approve-${s.id}`}>
                <CheckCircle size={13} /> Approve
              </button>
              <button onClick={() => act(s.id, "archived")} disabled={busy === `archived-${s.id}`}
                className="rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-medium text-stone-500 hover:bg-stone-100" data-testid={`queue-archive-${s.id}`}>
                Archive
              </button>
            </div>
          </div>
        ))}
        {queue.length === 0 && (
          <p className="px-4 py-12 text-center text-sm text-stone-400" data-testid="queue-empty">Queue is clear — every source has been reviewed.</p>
        )}
      </div>
    </section>
  );
}

export function MandatoryLanguageTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/knowledge/mandatory-language");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy("seed");
    try {
      const { data } = await api.post("/knowledge/mandatory-language/seed");
      toast.success(`Checklist ready: ${data.total} legacy sections`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const check = async () => {
    setBusy("check");
    try {
      const { data } = await api.post("/knowledge/mandatory-language/check", {}, { timeout: 60000 });
      toast.success(`Checked ${data.checked} required phrases against current policies`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (mid, phraseId, status) => {
    try {
      await api.put(`/knowledge/mandatory-language/${mid}/phrases/${phraseId}`, { status });
      toast.success("Status saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveNotes = async (mid, phraseId, notes) => {
    try {
      await api.put(`/knowledge/mandatory-language/${mid}/phrases/${phraseId}`, { notes });
      toast.success("Notes saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (items === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Loading checklist…</div>;

  const counts = { exact: 0, modified: 0, missing: 0, counsel_review: 0 };
  for (const it of items) {
    for (const ph of it.phrases) {
      counts[(ph.status || ph.auto_status || "missing")] = (counts[(ph.status || ph.auto_status || "missing")] || 0) + 1;
    }
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="mandatory-language-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Mandatory language checklist</h2>
          <StepNote testid="stepnote-language">
            Verbatim phrases surveyors look for. Auto-check compares each phrase against your current policy text and reports <strong>Exact / Modified / Missing</strong> — a Missing item is a policy edit waiting to happen, not a dead end. Your manual status always overrides the auto-check.
          </StepNote>
          <p className="mt-1 max-w-2xl text-xs text-stone-500">
            Verbatim underlined wording from the legacy policies — surveyors check for this exact language. Auto-check compares each phrase against current policy text; your manual status always wins.
          </p>
        </div>
        <div className="flex gap-2">
          {items.length === 0 && (
            <Button onClick={seed} disabled={busy === "seed"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="mandatory-seed-btn">
              {busy === "seed" ? "Building…" : "Build checklist from legacy extract"}
            </Button>
          )}
          {items.length > 0 && (
            <Button onClick={check} disabled={busy === "check"} variant="outline" className="border-stone-300" data-testid="mandatory-check-btn">
              <ArrowClockwise size={15} className="mr-1.5" /> {busy === "check" ? "Checking…" : "Re-check against policies"}
            </Button>
          )}
        </div>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 border-b border-stone-200 px-4 py-3" data-testid="mandatory-summary">
          {Object.entries(LANG_STATUS_META).map(([key, [label, cls]]) => (
            <span key={key} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`} data-testid={`mandatory-count-${key}`}>{label}: {counts[key] || 0}</span>
          ))}
        </div>
      )}
      <div className="divide-y divide-stone-100">
        {items.map((it) => (
          <div key={it.id} className="px-4 py-4" data-testid={`mandatory-item-${it.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-600">{it.legacy_ref}</span>
              <p className="text-sm font-semibold text-stone-900">{it.title}</p>
              <LangBadge status={it.overall} testid={`mandatory-overall-${it.id}`} />
            </div>
            <div className="mt-3 space-y-3">
              {it.phrases.map((ph) => (
                <div key={ph.id} className="rounded-lg border border-stone-200 p-3" data-testid={`mandatory-phrase-${ph.id}`}>
                  <p className="text-sm italic leading-relaxed text-stone-700">&ldquo;{ph.text}&rdquo;</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <LangBadge status={ph.status || ph.auto_status || "missing"} testid={`mandatory-phrase-status-${ph.id}`} />
                    {ph.status && <span className="text-stone-400">manual · auto says {ph.auto_status || "—"}</span>}
                    {ph.policy_title && <span className="text-stone-500">in policy: <span className="font-medium text-stone-700">{ph.policy_title}</span></span>}
                    <select value={ph.status || ""} onChange={(e) => setStatus(it.id, ph.id, e.target.value)}
                      className="ml-auto h-7 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`mandatory-status-select-${ph.id}`}>
                      <option value="">Auto</option>
                      <option value="exact">Exact</option>
                      <option value="modified">Modified</option>
                      <option value="missing">Missing</option>
                      <option value="counsel_review">Needs counsel review</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-stone-400 hover:text-stone-600" data-testid={`mandatory-notes-toggle-${it.id}`}>Section notes</summary>
              <div className="mt-2 flex gap-2">
                <Textarea defaultValue={it.notes} rows={2} className="text-sm" data-testid={`mandatory-notes-${it.id}`}
                  onBlur={(e) => e.target.value !== (it.notes || "") && saveNotes(it.id, it.phrases[0]?.id, e.target.value)} />
              </div>
            </details>
          </div>
        ))}
      </div>
    </section>
  );
}

export function GapReportTab() {
  const [report, setReport] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/knowledge/gap-report");
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [adding, setAdding] = useState(false);
  const [newGap, setNewGap] = useState({ text: "", source_id: "" });
  const [gapBusy, setGapBusy] = useState("");

  const addGap = async () => {
    if (newGap.text.trim().length < 5) {
      toast.error("Describe the gap (at least 5 characters)");
      return;
    }
    setGapBusy("add");
    try {
      await api.post("/knowledge/gaps", newGap);
      toast.success("Gap recorded — it stays on this report until you resolve it");
      setAdding(false);
      setNewGap({ text: "", source_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGapBusy("");
    }
  };

  const setGapStatus = async (g, status) => {
    setGapBusy(g.id);
    try {
      await api.put(`/knowledge/gaps/${g.id}`, { status });
      toast.success(status === "resolved" ? "Gap marked resolved" : "Gap reopened");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGapBusy("");
    }
  };

  const removeGap = async (g) => {
    if (!window.confirm(`Delete this manually recorded gap?\n\n"${g.text.slice(0, 140)}"`)) return;
    setGapBusy(g.id);
    try {
      await api.delete(`/knowledge/gaps/${g.id}`);
      toast.success("Gap deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGapBusy("");
    }
  };

  if (report === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Building gap report…</div>;

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="gap-report-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Source-to-policy gap report</h2>
          <p className="mt-1 text-xs text-stone-500">Approved sources only. A gap means a requirement source has no published policy, training, or orientation step mapped to it.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setAdding((v) => !v)} variant="outline" className="border-primary/40 text-accent-foreground" data-testid="gap-add-btn">+ Add gap manually</Button>
          <Button onClick={load} variant="outline" className="border-stone-300" data-testid="gap-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
          <a href={`${api.defaults.baseURL}/knowledge/gap-report.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="gap-export-pdf">
            <FileText size={15} /> Export PDF
          </a>
        </div>
      </div>
      {adding && (
        <div className="space-y-2 border-b border-stone-200 bg-accent/40 p-4" data-testid="gap-add-form">
          <Textarea value={newGap.text} onChange={(e) => setNewGap((g) => ({ ...g, text: e.target.value }))} rows={2} placeholder="Describe the gap you know about — e.g. 'No written policy covering X' or 'Topic Y is missing from the training program'" data-testid="gap-add-text" />
          <div className="flex flex-wrap gap-2">
            <select value={newGap.source_id} onChange={(e) => setNewGap((g) => ({ ...g, source_id: e.target.value }))} className="h-9 max-w-md rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="gap-add-source">
              <option value="">Not tied to a source</option>
              {report.sources.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
            <Button onClick={addGap} disabled={gapBusy === "add"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="gap-add-save">{gapBusy === "add" ? "Saving…" : "Save gap"}</Button>
            <Button variant="outline" onClick={() => setAdding(false)} className="border-stone-300" data-testid="gap-add-cancel">Cancel</Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 border-b border-stone-200 p-4 md:grid-cols-4" data-testid="gap-summary">
        {[["approved_sources", "Approved sources"], ["sources_with_gaps", "Sources with gaps"], ["unmapped_policies", "Unmapped policies"], ["manual_gaps_open", "Manual gaps open"]].map(([k, label]) => (
          <div key={k} className="rounded-lg border border-stone-200 p-3" data-testid={`gap-summary-${k}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{label}</p>
            <p className="mt-1 font-heading text-2xl font-semibold text-stone-900">{report.summary[k]}</p>
          </div>
        ))}
      </div>
      {report.sources.length === 0 && (
        <p className="px-4 py-10 text-center text-sm text-stone-400" data-testid="gap-no-sources">No approved sources yet — approve sources in the Review Queue first.</p>
      )}
      <div className="divide-y divide-stone-100">
        {report.sources.map((s) => (
          <div key={s.id} className="px-4 py-4" data-testid={`gap-source-${s.id}`}>
            <p className="text-sm font-semibold text-stone-900">{s.title}</p>
            <div className="mt-2 grid gap-3 text-xs sm:grid-cols-3">
              <div><p className="font-semibold uppercase tracking-wide text-stone-400">Policies</p>
                {s.policies.length === 0 ? <p className="mt-1 text-[#C0392B]">None mapped</p> : s.policies.map((p) => (
                  <p key={p.id} className="mt-1 text-stone-700">{p.title} <span className={`font-medium ${p.status === "published" ? "text-[#1e8449]" : "text-[#B7791F]"}`}>({p.status})</span></p>
                ))}</div>
              <div><p className="font-semibold uppercase tracking-wide text-stone-400">Trainings</p>
                {s.trainings.length === 0 ? <p className="mt-1 text-stone-400">None linked</p> : s.trainings.map((t) => <p key={t.id} className="mt-1 text-stone-700">{t.title}</p>)}</div>
              <div><p className="font-semibold uppercase tracking-wide text-stone-400">Orientation</p>
                {s.orientation.length === 0 ? <p className="mt-1 text-stone-400">None linked</p> : s.orientation.map((o) => <p key={o.id} className="mt-1 text-stone-700">{o.title}</p>)}</div>
            </div>
            {s.gaps.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`gap-flags-${s.id}`}>
                {s.gaps.map((g) => <span key={g} className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 text-xs font-medium text-[#C0392B]">{g}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
      {(report.manual_gaps || []).length > 0 && (
        <div className="border-t border-stone-200 p-4" data-testid="gap-manual">
          <h3 className="text-sm font-semibold text-stone-900">Manually recorded gaps ({(report.manual_gaps || []).filter((g) => g.status === "open").length} open)</h3>
          <div className="mt-2 divide-y divide-stone-100">
            {(report.manual_gaps || []).map((g) => (
              <div key={g.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`gap-manual-${g.id}`}>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${g.status === "open" ? "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"}`} data-testid={`gap-manual-status-${g.id}`}>{g.status}</span>
                <span className="min-w-0 flex-1 text-sm text-stone-700">{g.text}{g.source_title ? <span className="ml-2 text-xs text-stone-400">· {g.source_title}</span> : null}</span>
                <Button size="sm" variant="outline" onClick={() => setGapStatus(g, g.status === "open" ? "resolved" : "open")} disabled={gapBusy === g.id} className="border-stone-300 text-xs" data-testid={`gap-manual-toggle-${g.id}`}>{g.status === "open" ? "Mark resolved" : "Reopen"}</Button>
                <button title="Delete" onClick={() => removeGap(g)} disabled={gapBusy === g.id} className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-[#C0392B]" data-testid={`gap-manual-delete-${g.id}`}><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {report.unmapped_policies.length > 0 && (
        <div className="border-t border-stone-200 p-4" data-testid="gap-unmapped">
          <h3 className="text-sm font-semibold text-stone-900">Policies with no approved-source mapping ({report.unmapped_policies.length})</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.unmapped_policies.map((p) => (
              <span key={p.id} className="rounded-full bg-stone-100 px-2.5 py-1 text-xs text-stone-600" data-testid={`gap-unmapped-${p.id}`}>{p.title} · {p.status}</span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function CoverageTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/knowledge/coverage-matrix");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Building coverage matrix…</div>;

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="coverage-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Policy coverage matrix</h2>
          <StepNote testid="stepnote-coverage">
            One row per policy: which approved sources back it, linked trainings with completion counts, orientation steps, signoffs. This fills in automatically as you approve sources and mappings — empty columns mean missing links, not missing data.
          </StepNote>
          <p className="mt-1 text-xs text-stone-500">One row per policy: approved sources behind it, linked trainings + completions, orientation steps, guideline signoffs. {data.approved_source_count === 0 ? "Approve sources first — mappings only count once approved." : `${data.approved_source_count} approved source(s) feeding this matrix.`}</p>
        </div>
        <Button onClick={load} variant="outline" className="border-stone-300" data-testid="coverage-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
            <tr>
              <th className="px-4 py-3">Policy</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Approved sources</th>
              <th className="px-4 py-3">Trainings</th>
              <th className="px-4 py-3">Orientation</th>
              <th className="px-4 py-3">Signoffs</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-100 last:border-0" data-testid={`coverage-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-stone-900">{r.title}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${r.status === "published" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#7c4a03]"}`} data-testid={`coverage-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-stone-600">{r.sources.length ? r.sources.join("; ") : <span className="text-stone-400">—</span>}</td>
                <td className="px-4 py-3 text-xs text-stone-600">{r.trainings.length ? r.trainings.map((t) => t.title).join("; ") : <span className="text-stone-400">—</span>}{r.training_completions ? <span className="block text-stone-400">{r.training_completions} completions</span> : null}</td>
                <td className="px-4 py-3 text-xs text-stone-600">{r.orientation.length ? r.orientation.map((o) => o.title).join("; ") : <span className="text-stone-400">—</span>}</td>
                <td className="px-4 py-3 text-xs text-stone-600">{r.signoffs == null ? <span className="text-stone-400">—</span> : `${r.signoffs} / ${r.signoff_total}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ChangelogTab() {
  const [entries, setEntries] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/knowledge/change-log", { params: { limit: 300 } });
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (entries === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Loading change log…</div>;

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="changelog-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Change log</h2>
          <StepNote testid="stepnote-changelog">
            Every knowledge-base and policy action, in order: uploads, approvals, mappings, requirement reviews, policy checks, drafts, edits, deletions — who did it and when. This is your audit trail.
          </StepNote>
        </div>
        <Button onClick={load} variant="outline" className="border-stone-300" data-testid="changelog-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
      </div>
      <div className="divide-y divide-stone-100" data-testid="changelog-list">
        {entries.map((e, i) => (
          <div key={i} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2" data-testid={`changelog-entry-${i}`}>
            <span className="w-40 shrink-0 text-xs text-stone-400">{new Date(e.at).toLocaleString()}</span>
            <span className="w-44 shrink-0 truncate text-xs font-medium text-stone-500">{e.actor}</span>
            <span className="min-w-0 flex-1 text-sm text-stone-700">{e.action}</span>
          </div>
        ))}
        {entries.length === 0 && <p className="p-8 text-center text-sm text-stone-400">No knowledge or policy changes logged yet.</p>}
      </div>
    </section>
  );
}

const POLICY_VERDICT_META = {
  aligned: ["Aligned", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  partial: ["Partial", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  gaps: ["Gaps found", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  conflict: ["Conflict", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
};
const FINDING_META = {
  covered: ["Covered", "text-[#1e8449]"],
  gap: ["Gap", "text-[#C0392B]"],
  conflict: ["Conflict", "text-[#C0392B]"],
  outdated: ["Outdated", "text-[#8a5d14]"],
};

export function PolicyCheckTab({ policies }) {
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState("");
  const [batch, setBatch] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [history, setHistory] = useState({});
  const [impact, setImpact] = useState({});
  const [sel, setSel] = useState({});
  const [qaInput, setQaInput] = useState({});
  const [qaBusy, setQaBusy] = useState("");
  const batchStop = useRef(false);
  const load = useCallback(async () => {
    try {
      const [{ data: cd }, { data: dd }] = await Promise.all([
        api.get("/knowledge/policy-checks"),
        api.get("/knowledge/policy-drafts", { params: { status: "pending" } }),
      ]);
      setChecks(cd.checks || []);
      setDrafts(Object.fromEntries((dd.drafts || []).map((d) => [d.policy_id, d])));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (checks === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Loading policy checks…</div>;
  const byPid = Object.fromEntries(checks.map((c) => [c.policy_id, c]));

  const runOne = async (pid) => {
    setBusy(pid);
    try {
      const { data } = await api.post(`/knowledge/policy-check/${pid}`, {}, { timeout: 180000 });
      toast.success(`${data.check.policy_title}: ${data.check.verdict}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const runAll = async () => {
    batchStop.current = false;
    let failed = 0;
    setBatch({ i: 0, total: policies.length, title: "", failed: 0 });
    for (let i = 0; i < policies.length; i++) {
      if (batchStop.current) break;
      setBatch({ i, total: policies.length, title: policies[i].title, failed });
      try {
        await api.post(`/knowledge/policy-check/${policies[i].id}`, {}, { timeout: 180000 });
      } catch { failed++; }
    }
    setBatch(null);
    toast.success(`Policy check finished — ${policies.length - failed} checked${failed ? `, ${failed} skipped (no text content)` : ""}`);
    load();
  };

  const draftFix = async (pid) => {
    setBusy(`d-${pid}`);
    try {
      const { data } = await api.post(`/knowledge/policy-check/${pid}/draft-fix`, {}, { timeout: 180000 });
      setDrafts((d) => ({ ...d, [pid]: data.draft }));
      setOpen(pid);
      toast.success(`Draft ready — ${data.draft.proposals.length} proposal(s) to review`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const decideDraft = async (pid, did, decision) => {
    setBusy(`${decision}-${did}`);
    try {
      const { data } = await api.post(`/knowledge/policy-drafts/${did}/${decision}`, decision === "accept" ? { selected: sel[did] ?? null } : {});
      setDrafts((d) => { const c = { ...d }; delete c[pid]; return c; });
      toast.success(decision === "accept"
        ? `Applied as v${data.version ?? "?"} — ${data.applied ?? 0} section(s) replaced in place, ${data.appended ?? 0} appended at the bottom of the document. Previous version kept in History.`
        : "Draft rejected — nothing changed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const toggleHistory = async (pid) => {
    if (history[pid]) {
      setHistory((h) => { const c = { ...h }; delete c[pid]; return c; });
      return;
    }
    try {
      const { data } = await api.get(`/policies/${pid}/versions`);
      setHistory((h) => ({ ...h, [pid]: data.versions || [] }));
      setOpen(pid);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const draftSel = (draft) => sel[draft.id] ?? draft.proposals.map((_, i) => i);

  const toggleSel = (did, i, total) => setSel((s) => {
    const cur = s[did] ?? Array.from({ length: total }, (_, j) => j);
    return { ...s, [did]: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i] };
  });

  const askQ = async (pid) => {
    const q = (qaInput[pid] || "").trim();
    if (!q) return;
    setQaBusy(pid);
    try {
      await api.post(`/knowledge/policy-check/${pid}/ask`, { question: q }, { timeout: 120000 });
      setQaInput((s) => ({ ...s, [pid]: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQaBusy("");
    }
  };

  const toggleImpact = async (pid) => {
    if (impact[pid]) {
      setImpact((h) => { const c = { ...h }; delete c[pid]; return c; });
      return;
    }
    try {
      const { data } = await api.get(`/policies/${pid}/impact`);
      setImpact((h) => ({ ...h, [pid]: data }));
      setOpen(pid);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const dismissFinding = async (pid, idx) => {
    setBusy(`df-${pid}-${idx}`);
    try {
      await api.post(`/knowledge/policy-check/${pid}/dismiss-finding`, { index: idx });
      toast.success("Finding removed — recorded in the audit log");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const badCitCount = (c) => (c.findings || []).filter((f) => f.citation && f.citation_verified === false).length;

  const confirm = async (pid) => {
    setBusy(`c-${pid}`);
    try {
      await api.post(`/knowledge/policy-check/${pid}/confirm`);
      toast.success("Policy stamped as KB-verified");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="policy-check-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Policy check against the Knowledge Base</h2>
          <StepNote testid="stepnote-policy-check">
            Runs each policy against your approved sources and extracted requirements: what it covers, what&rsquo;s missing, what conflicts. AI finds, you decide — Confirming stamps the policy KB-verified with the date. Nothing rewrites your policy text.
          </StepNote>
        </div>
        <div className="flex gap-2">
          {batch ? (
            <Button variant="outline" onClick={() => { batchStop.current = true; }} className="border-stone-300" data-testid="policy-check-stop">Stop ({batch.i + 1}/{batch.total})</Button>
          ) : (
            <Button onClick={runAll} disabled={!!busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="policy-check-all">Check all {policies.length} policies</Button>
          )}
          <Button onClick={load} variant="outline" className="border-stone-300" data-testid="policy-check-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>
      {batch && <p className="border-b border-stone-100 bg-accent/40 px-4 py-2 text-xs text-stone-600" data-testid="policy-check-progress">Checking {batch.i + 1}/{batch.total}: {batch.title}{batch.failed ? ` — ${batch.failed} skipped` : ""}</p>}
      <div className="divide-y divide-stone-100">
        {policies.map((p) => {
          const c = byPid[p.id];
          const meta = c ? POLICY_VERDICT_META[c.verdict] || POLICY_VERDICT_META.partial : null;
          return (
            <div key={p.id} className="px-4 py-3" data-testid={`policy-check-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setOpen(open === p.id ? "" : p.id)} className="min-w-0 flex-1 text-left" data-testid={`policy-check-expand-${p.id}`}>
                  <span className="text-sm font-semibold text-stone-900">{p.title}</span>
                  <span className="ml-2 rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-bold text-stone-500" data-testid={`policy-version-${p.id}`}>v{p.version || 1}</span>
                  {c && <span className="ml-2 text-xs text-stone-400">{new Date(c.created_at).toLocaleDateString()} · {c.requirements_checked} requirements · {c.sources_referenced} sources · {(c.findings || []).length} findings</span>}
                  {c && badCitCount(c) > 0 && <span className="ml-2 rounded border border-[#C0392B]/30 bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#C0392B]" data-testid={`policy-badcit-count-${p.id}`}>{badCitCount(c)} bad citation{badCitCount(c) > 1 ? "s" : ""}</span>}
                </button>
                {c ? <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${meta[1]}`} data-testid={`policy-verdict-${p.id}`}>{meta[0]}</span>
                    : <span className="rounded-full border border-stone-300 px-2.5 py-1 text-xs text-stone-400" data-testid={`policy-verdict-${p.id}`}>Not checked</span>}
                {c && (c.status === "confirmed"
                  ? <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2.5 py-1 text-xs font-medium text-[#1e8449]" data-testid={`policy-verified-${p.id}`}>KB-verified</span>
                  : <Button size="sm" variant="outline" onClick={() => confirm(p.id)} disabled={busy === `c-${p.id}` || badCitCount(c) > 0}
                      title={badCitCount(c) > 0 ? "Remove or resolve findings with unverified citations first" : "Stamp this policy as KB-verified"}
                      className="border-stone-300 text-xs" data-testid={`policy-confirm-${p.id}`}>Confirm</Button>)}
                {c && c.verdict !== "aligned" && (
                  <Button size="sm" variant="outline" onClick={() => draftFix(p.id)} disabled={busy === `d-${p.id}`} className="border-primary/40 text-xs text-accent-foreground" data-testid={`policy-draft-btn-${p.id}`}>{busy === `d-${p.id}` ? "Drafting…" : "Draft the fix"}</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => runOne(p.id)} disabled={busy === p.id} className="border-primary/40 text-xs text-accent-foreground" data-testid={`policy-run-${p.id}`}>{busy === p.id ? "Checking…" : c ? "Re-check" : "Check"}</Button>
                <Button size="sm" variant="outline" onClick={() => toggleHistory(p.id)} className="border-stone-300 text-xs" data-testid={`policy-history-btn-${p.id}`}>History</Button>
                <Button size="sm" variant="outline" onClick={() => toggleImpact(p.id)} className="border-stone-300 text-xs" data-testid={`policy-impact-btn-${p.id}`}>Impact</Button>
              </div>
              {open === p.id && drafts[p.id] && (
                <div className="mt-2 rounded-md border border-primary/30 bg-white p-3" data-testid={`policy-draft-card-${p.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent-foreground">AI draft fix — review each proposal</p>
                  <div className="mt-2 space-y-3">
                    {drafts[p.id].proposals.map((pr, i) => (
                      <div key={i} className="rounded border border-stone-200 p-2.5" data-testid={`draft-proposal-${drafts[p.id].id}-${i}`}>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={draftSel(drafts[p.id]).includes(i)} onChange={() => toggleSel(drafts[p.id].id, i, drafts[p.id].proposals.length)} data-testid={`draft-check-${drafts[p.id].id}-${i}`} />
                          <p className="text-xs font-semibold text-stone-700">{pr.section}</p>
                        </label>
                        {pr.original_excerpt
                          ? <p className="mt-1 rounded bg-[#C0392B]/5 px-2 py-1 text-xs text-[#C0392B] line-through">{pr.original_excerpt}</p>
                          : <p className="mt-1 text-xs italic text-stone-400">(new text — nothing replaced)</p>}
                        <p className="mt-1 rounded bg-[#27AE60]/5 px-2 py-1 text-xs text-[#1e8449]">{pr.proposed_text}</p>
                        <p className="mt-1 text-[11px] text-stone-400">{pr.rationale}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => decideDraft(p.id, drafts[p.id].id, "accept")} disabled={!!busy || draftSel(drafts[p.id]).length === 0} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`draft-accept-${drafts[p.id].id}`}>Accept {draftSel(drafts[p.id]).length} selected — apply as new version</Button>
                    <Button size="sm" variant="outline" onClick={() => decideDraft(p.id, drafts[p.id].id, "reject")} disabled={!!busy} className="border-stone-300" data-testid={`draft-reject-${drafts[p.id].id}`}>Reject</Button>
                  </div>
                </div>
              )}
              {open === p.id && history[p.id] && (
                <div className="mt-2 rounded-md border border-stone-200 p-3" data-testid={`policy-versions-${p.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Version history</p>
                  {history[p.id].length === 0
                    ? <p className="mt-1 text-xs text-stone-400">No prior versions recorded — snapshots start from the next edit.</p>
                    : <ul className="mt-1 space-y-1">{history[p.id].map((v) => <li key={v.id} className="text-xs text-stone-600">v{v.version} — saved {new Date(v.saved_at).toLocaleString()} by {v.saved_by}{v.note ? ` (${v.note})` : ""}</li>)}</ul>}
                </div>
              )}
              {open === p.id && impact[p.id] && (
                <div className="mt-2 rounded-md border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid={`policy-impact-${p.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8a5d14]">Downstream dependencies — what this policy touches</p>
                  <div className="mt-1.5 grid gap-1.5 text-xs text-stone-700 sm:grid-cols-2">
                    <p>Linked requirements: <strong>{impact[p.id].requirements.length}</strong></p>
                    <p>Mapped sources: <strong>{impact[p.id].sources.length}</strong></p>
                    <p>Training topics reached: <strong>{impact[p.id].topics.length}</strong></p>
                    <p>Orientation steps reached: <strong>{impact[p.id].steps.length}</strong></p>
                    <p>Staff attestations on current text: <strong>{impact[p.id].signed_count}</strong></p>
                    <p>Policies cross-referencing it: <strong>{impact[p.id].cross_refs.length}</strong></p>
                  </div>
                  {impact[p.id].cross_refs.length > 0 && <p className="mt-1.5 text-xs text-stone-500">Cross-refs: {impact[p.id].cross_refs.join("; ")}</p>}
                  {impact[p.id].topics.length > 0 && <p className="mt-1 text-xs text-stone-500">Trainings: {impact[p.id].topics.join("; ")}</p>}
                  {impact[p.id].signed_count > 0 && <p className="mt-1.5 text-xs font-medium text-[#8a5d14]">Note: {impact[p.id].signed_count} staff signed the current version — after a content change, consider re-attestation.</p>}
                </div>
              )}
              {open === p.id && c && (
                <div className="mt-2 rounded-md bg-stone-50 p-3" data-testid={`policy-findings-${p.id}`}>
                  <p className="text-sm text-stone-700">{c.summary}</p>
                  {(c.findings || []).length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {c.findings.map((f, i) => {
                        const fm = FINDING_META[f.type] || ["Note", "text-stone-500"];
                        return <li key={i} className="text-xs text-stone-600"><span className={`mr-1.5 font-bold uppercase ${fm[1]}`}>{fm[0]}</span>{f.text}{f.citation ? <span className="text-stone-400"> — {f.citation}</span> : null}</li>;
                      })}
                    </ul>
                  )}
                  {(c.suggestions || []).length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Suggestions</p>
                      <ul className="mt-1 list-disc pl-4 text-xs text-stone-600">{c.suggestions.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  <div className="mt-3 border-t border-stone-200 pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Ask about this check</p>
                    {(c.qa || []).map((q, i) => (
                      <div key={i} className="mt-2 text-xs" data-testid={`policy-qa-${p.id}-${i}`}>
                        <p className="font-medium text-stone-700">Q: {q.q}</p>
                        <p className="mt-0.5 whitespace-pre-wrap text-stone-600">A: {q.a}</p>
                      </div>
                    ))}
                    <div className="mt-2 flex gap-2">
                      <input value={qaInput[p.id] || ""} onChange={(e) => setQaInput((s) => ({ ...s, [p.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") askQ(p.id); }}
                        placeholder="e.g. Why is this a conflict? Which source says that?"
                        className="h-8 flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`policy-qa-input-${p.id}`} />
                      <Button size="sm" variant="outline" onClick={() => askQ(p.id)} disabled={qaBusy === p.id} className="border-primary/40 text-xs text-accent-foreground" data-testid={`policy-qa-ask-${p.id}`}>{qaBusy === p.id ? "Asking…" : "Ask"}</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SummaryTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/knowledge/posture");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Building summary…</div>;

  const req = data.requirements?.by_status || {};
  const reviewed = (req.covered || 0) + (req.partial || 0) + (req.missing || 0) + (req.counsel_review || 0);
  const reqTotal = (data.requirements?.total || 0) - (req.superseded || 0);
  const srcBy = data.sources?.by_status || {};
  const polBy = data.policies?.by_status || {};

  const rows = (items) => items.map(([label, value, hint]) => (
    <div key={label} className="flex items-baseline justify-between border-b border-stone-100 py-2 last:border-0">
      <span className="text-sm text-stone-600">{label}</span>
      <span className="text-sm font-semibold text-stone-900">{value}{hint ? <span className="ml-2 text-xs font-normal text-stone-400">{hint}</span> : null}</span>
    </div>
  ));

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="summary-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-stone-200 p-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-stone-900">Compliance summary</h2>
          <StepNote testid="stepnote-summary">
            A static snapshot computed straight from your data — no AI involved, always current. Print or save as PDF for a survey, a payer review, or your own binder.
          </StepNote>
          <p className="mt-1 text-xs text-stone-500">Silver Home Care · Generated {new Date(data.generated_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={load} variant="outline" className="border-stone-300" data-testid="summary-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
          <Button onClick={() => window.print()} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="summary-print">Print / Save PDF</Button>
        </div>
      </div>
      <div className="grid gap-4 p-4 md:grid-cols-2">
        <div className="rounded-lg border border-stone-200 p-4" data-testid="summary-sources">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">Foundation documents</h3>
          <div className="mt-2">{rows([
            ["Source documents (latest versions)", data.sources?.total || 0],
            ["Approved", srcBy.approved || 0],
            ["Awaiting review", srcBy.needs_review || 0],
            ["Archived", srcBy.archived || 0],
          ])}</div>
        </div>
        <div className="rounded-lg border border-stone-200 p-4" data-testid="summary-requirements">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">Requirements review</h3>
          <div className="mt-2">{rows([
            ["Requirements identified", reqTotal],
            ["Reviewed", reviewed, reqTotal ? `${Math.round((reviewed / reqTotal) * 100)}%` : ""],
            ["Covered by policy", req.covered || 0],
            ["Partial / missing (gaps)", `${req.partial || 0} / ${req.missing || 0}`],
            ["Pending review", req.pending_review || 0],
            ["Superseded by newer version", req.superseded || 0],
          ])}</div>
        </div>
        <div className="rounded-lg border border-stone-200 p-4" data-testid="summary-policies">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">Policies</h3>
          <div className="mt-2">{rows([
            ["Policy documents", data.policies?.total || 0],
            ["Published", polBy.published || 0],
            ["Draft", polBy.draft || 0],
            ["Backed by an approved source", data.policies?.backed_by_approved_source || 0],
          ])}</div>
        </div>
        <div className="rounded-lg border border-stone-200 p-4" data-testid="summary-training">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">Training & orientation</h3>
          <div className="mt-2">{rows([
            ["Active training topics", data.training_topics || 0],
            ["Orientation steps", data.orientation_steps || 0],
            ["Training completions on record", data.training_completions || 0],
          ])}</div>
        </div>
      </div>
      <p className="border-t border-stone-200 px-4 py-3 text-xs text-stone-400">Silver Home Care · Confidential — internal compliance record. Computed from live system data; reflects review status as of the timestamp above.</p>
    </section>
  );
}

export function RequirementsTab({ policies, topics, sources = [] }) {
  const PAGE_SIZE = 100;
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);
  const [newReq, setNewReq] = useState({ source_id: "", text: "", citation: "" });
  const [editing, setEditing] = useState("");
  const [editDraft, setEditDraft] = useState({ text: "", citation: "" });

  const addRequirement = async () => {
    if (newReq.text.trim().length < 10) {
      toast.error("Describe the requirement (at least a short sentence)");
      return;
    }
    setBusy("add");
    try {
      await api.post("/knowledge/requirements", newReq);
      toast.success("Requirement added — mark its status like any other");
      setNewReq({ source_id: "", text: "", citation: "" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setEditDraft({ text: r.text, citation: r.citation || "" });
  };

  const saveEdit = async () => {
    if (editDraft.text.trim().length < 10) {
      toast.error("Requirement text is too short");
      return;
    }
    setBusy(`edit-${editing}`);
    try {
      await api.put(`/knowledge/requirements/${editing}`, editDraft);
      toast.success("Requirement wording updated");
      setEditing("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const toggleMonitorable = async (r) => {
    setBusy(`mon-${r.id}`);
    try {
      await api.put(`/knowledge/requirements/${r.id}`, { monitorable: !r.monitorable });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const removeRequirement = async (r) => {
    if (!window.confirm(`Delete just this one requirement?\n\n"${r.text.slice(0, 140)}"\n\nThe source document and all other requirements are untouched.`)) return;
    setBusy(`del-${r.id}`);
    try {
      await api.delete(`/knowledge/requirements/${r.id}`);
      toast.success("Requirement deleted — everything else is untouched");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const [recs, setRecs] = useState(null);
  const [recSel, setRecSel] = useState({});

  const openRecs = async () => {
    setBusy("recs-load");
    try {
      const { data } = await api.get("/knowledge/requirements", { params: { recommended: true } });
      const items = data.items || [];
      setRecs(items);
      setRecSel(Object.fromEntries(items.filter((r) => r.recommended_policy_id).map((r) => [r.id, true])));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const applySelectedRecs = async () => {
    const ids = Object.keys(recSel).filter((k) => recSel[k]);
    if (!ids.length) return;
    setBusy("recs-apply");
    try {
      const { data } = await api.post("/knowledge/requirements/apply-recommendations", { ids });
      toast.success(`Applied ${data.applied} recommendation(s)`);
      setRecs(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const applyRec = async (r) => {
    setBusy(`rec-${r.id}`);
    try {
      await api.put(`/knowledge/requirements/${r.id}`, { policy_ids: [r.recommended_policy_id] });
      toast.success("Requirement assigned to the recommended policy");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const createPolicyFor = async (r) => {
    setBusy(`rec-${r.id}`);
    try {
      const { data } = await api.post("/policies", {
        title: r.recommended_new_policy,
        content: `Draft created from a Knowledge Base recommendation.\n\nSource requirement: ${r.text}\nCitation: ${r.citation || "—"}\nSource: ${r.source_title || "manual entry"}`,
        category: "Compliance",
      });
      const newId = (data.policy || data).id;
      if (newId) await api.put(`/knowledge/requirements/${r.id}`, { policy_ids: [newId] });
      toast.success(`Draft policy "${r.recommended_new_policy}" created and linked`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const recommendHomes = async () => {
    for (let i = 0; i < 40; i++) {
      setBusy(`recommend-${i + 1}`);
      try {
        const { data } = await api.post("/knowledge/requirements/recommend-homes", {}, { timeout: 180000 });
        if (!data.recommended || data.done) break;
      } catch (e) {
        toast.error(formatApiError(e));
        break;
      }
    }
    setBusy("");
    toast.success("Recommendations updated — rows now show AI-suggested policy homes");
    load();
  };

  const REQ_STATUS_META = {
    pending_review: ["Pending review", "border-stone-300 bg-stone-100 text-stone-500"],
    covered: ["Covered", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
    partial: ["Partial", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
    missing: ["Missing", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
    counsel_review: ["Counsel review", "border-primary/30 bg-accent text-accent-foreground"],
    superseded: ["Superseded", "border-stone-200 bg-stone-50 text-stone-400"],
  };

  const load = useCallback(async () => {
    try {
      const q = [statusFilter && `status=${statusFilter}`, sourceFilter && `source_id=${sourceFilter}`, `skip=${page * PAGE_SIZE}`, `limit=${PAGE_SIZE}`].filter(Boolean).join("&");
      const [r, s] = await Promise.all([
        api.get(`/knowledge/requirements${q ? `?${q}` : ""}`),
        api.get("/knowledge/requirements/summary"),
      ]);
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setSummary(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [statusFilter, sourceFilter, page]);
  useEffect(() => { load(); }, [load]);

  const save = async (rid) => {
    const d = drafts[rid] || {};
    setBusy(rid);
    try {
      await api.put(`/knowledge/requirements/${rid}`, d);
      toast.success("Requirement review saved");
      setDrafts((s) => ({ ...s, [rid]: {} }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (items === null) return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-400">Loading requirements…</div>;

  const reqSources = sources.map((s) => [s.id, s.title]);
  const totals = summary?.totals || {};

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="requirements-panel">
      <div className="border-b border-stone-200 p-4">
        <h2 className="font-heading text-xl font-semibold text-stone-900">Requirement coverage review</h2>
        <StepNote step="Step 4 of 5" testid="stepnote-requirements">
          This is your survey punch list. Each row is one obligation pulled from a source document, with its citation. You decide: <strong>covered</strong> (a policy handles it), <strong>partial</strong>, <strong>missing</strong> (gap — write or fix a policy), or <strong>counsel review</strong>. Nothing here changes staff, policies, or training by itself. If the AI missed an obligation, use Add requirement. If one is worded wrong, edit it; if it doesn&rsquo;t belong, delete it — every row is independent, never all-or-nothing.
        </StepNote>
        <div className="mt-2 max-w-3xl rounded-md border border-[#B7791F]/30 bg-[#B7791F]/5 p-3 text-xs text-stone-600" data-testid="scope-sweep-note">
          <p className="font-semibold text-[#8a5d14]">Agency scope sweep (one-time housekeeping)</p>
          <p className="mt-1">Silver Home Care is a Pennsylvania <strong>non-medical</strong> home care agency (licensed home care facility). Requirements extracted before this scope rule was enforced may include items meant for medical or residential providers — those should never become policy or training. To sweep them: use the status filter chips below to page through rows, and for anything that doesn&rsquo;t apply to a non-medical home care facility either set it to <strong>Superseded</strong> (keeps history, removes it from active review), edit the wording, or delete just that row. Every row is independent — a wrong extraction never invalidates its source. New AI analysis already applies the scope rule, so this only needs doing once. Full walkthrough: <a href="/guide" className="font-medium text-primary underline">Guide</a>.</p>
        </div>
        <p className="mt-1 max-w-3xl text-xs text-stone-500">
          AI extracts discrete requirements from approved sources with citations. You confirm each one — covered / partial / missing / counsel review — and approve the policy + training links. AI suggestions never apply themselves.
        </p>
        {Object.keys(totals).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="requirements-summary">
            {Object.entries(REQ_STATUS_META).map(([key, [label, cls]]) => (
              <button key={key} onClick={() => { setStatusFilter(statusFilter === key ? "" : key); setPage(0); }}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${cls} ${statusFilter === key ? "ring-2 ring-primary/40" : ""}`} data-testid={`req-filter-${key}`}>
                {label}: {totals[key] || 0}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 border-b border-stone-200 p-4">
        <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }} className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="req-source-filter">
          <option value="">All sources</option>
          {reqSources.map(([sid, title]) => <option key={sid} value={sid}>{title}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={recommendHomes} disabled={!!busy} className="border-primary/40 text-accent-foreground" data-testid="req-recommend-btn">
          {busy.startsWith("recommend-") ? "Recommending…" : "Recommend homes (AI)"}
        </Button>
        <Button variant="outline" size="sm" onClick={openRecs} disabled={busy === "recs-load"} className="border-stone-300" data-testid="req-review-recs-btn">
          {busy === "recs-load" ? "Loading…" : "Review recommendations"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAdding(!adding)} className="border-stone-300" data-testid="req-add-btn">
          {adding ? "Close" : "+ Add requirement manually"}
        </Button>
      </div>
      {adding && (
        <div className="space-y-2 border-b border-stone-200 bg-accent/40 p-4" data-testid="req-add-form">
          <p className="text-xs font-semibold text-stone-600">Add an obligation the AI missed — it joins the list as pending review, exactly like an extracted one.</p>
          <select value={newReq.source_id} onChange={(e) => setNewReq({ ...newReq, source_id: e.target.value })} className="h-9 w-full max-w-md rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="req-add-source">
            <option value="">Source document (optional)…</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <input value={newReq.text} onChange={(e) => setNewReq({ ...newReq, text: e.target.value })} placeholder="Requirement, e.g. &quot;Agency must keep a copy of the current chapter on file&quot;"
            className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="req-add-text" />
          <div className="flex gap-2">
            <input value={newReq.citation} onChange={(e) => setNewReq({ ...newReq, citation: e.target.value })} placeholder="Citation (e.g. § 611.4) — optional"
              className="h-9 w-64 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="req-add-citation" />
            <Button size="sm" onClick={addRequirement} disabled={busy === "add"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="req-add-save">
              {busy === "add" ? "Adding…" : "Add requirement"}
            </Button>
          </div>
        </div>
      )}
      <div className="divide-y divide-stone-100">
        {items.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-stone-400" data-testid="req-empty">
            No requirements here. Approve a source in the Review Queue, open it, and click &ldquo;Extract requirements&rdquo;.
          </p>
        )}
        {items.map((r) => {
          const draft = drafts[r.id] || {};
          const dirty = Object.keys(draft).length > 0;
          return (
            <div key={r.id} className="px-4 py-4" data-testid={`req-item-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {editing === r.id ? (
                    <div className="space-y-2" data-testid={`req-edit-form-${r.id}`}>
                      <Textarea value={editDraft.text} onChange={(e) => setEditDraft((d) => ({ ...d, text: e.target.value }))} rows={3} className="text-sm" data-testid={`req-edit-text-${r.id}`} />
                      <div className="flex flex-wrap gap-2">
                        <input value={editDraft.citation} onChange={(e) => setEditDraft((d) => ({ ...d, citation: e.target.value }))} placeholder="Citation (optional)"
                          className="h-8 w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`req-edit-citation-${r.id}`} />
                        <Button size="sm" onClick={saveEdit} disabled={busy === `edit-${r.id}`} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`req-edit-save-${r.id}`}>
                          {busy === `edit-${r.id}` ? "Saving…" : "Save wording"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing("")} className="border-stone-300" data-testid={`req-edit-cancel-${r.id}`}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed text-stone-800">{r.text}</p>
                  )}
                  <p className="mt-1 text-xs text-stone-400">
                    {r.source_title}{r.citation ? ` · ${r.citation}` : ""}{r.section_title ? ` · ${r.section_title}` : ""}{r.page_start ? ` · p.${r.page_start}` : ""}
                    {r.manual ? <span className="ml-2 rounded bg-stone-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">manual</span> : null}
                    {r.ai_coverage && <span className="ml-2 italic">AI thinks: {r.ai_coverage}</span>}
                    {r.recommended_policy_id && !(r.approved_policy_ids || []).length ? (
                      <span className="ml-2 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground" title={r.recommendation_rationale || ""}>
                        AI home: {(policies.find((x) => x.id === r.recommended_policy_id) || {}).title || "policy"}
                        <button onClick={() => applyRec(r)} disabled={busy === `rec-${r.id}`} className="underline" data-testid={`req-apply-rec-${r.id}`}>Apply</button>
                      </span>
                    ) : null}
                    {r.recommended_new_policy && !r.recommended_policy_id && !(r.approved_policy_ids || []).length ? (
                      <span className="ml-2 inline-flex items-center gap-1 rounded bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a5d14]" title={r.recommendation_rationale || ""}>
                        No policy exists — AI suggests: {r.recommended_new_policy}
                        <button onClick={() => createPolicyFor(r)} disabled={busy === `rec-${r.id}`} className="underline" data-testid={`req-create-policy-${r.id}`}>Create draft</button>
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button title={r.monitorable ? "Monitorable in-app (click to unset)" : "Mark as monitorable in-app"} onClick={() => toggleMonitorable(r)} disabled={busy === `mon-${r.id}`}
                    className={`rounded px-1.5 py-1 text-[10px] font-bold uppercase tracking-wide ${r.monitorable ? "bg-accent text-accent-foreground" : "text-stone-300 hover:bg-stone-100 hover:text-stone-500"}`}
                    data-testid={`req-monitorable-${r.id}`}>App</button>
                  <button title="Edit wording" onClick={() => startEdit(r)} className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-primary" data-testid={`req-edit-btn-${r.id}`}><PencilSimple size={15} /></button>
                  <button title="Delete just this requirement" onClick={() => removeRequirement(r)} disabled={busy === `del-${r.id}`} className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-[#C0392B]" data-testid={`req-delete-btn-${r.id}`}><Trash size={15} /></button>
                  <LangBadge status={r.status} testid={`req-status-${r.id}`} />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select value={draft.status ?? r.status} onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...draft, status: e.target.value } }))}
                  className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`req-status-select-${r.id}`}>
                  {Object.entries(REQ_STATUS_META).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
                </select>
                <select value={draft.policy_ids?.[0] ?? (r.approved_policy_ids?.[0] || r.suggested_policy_ids?.[0] || "")}
                  onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...draft, policy_ids: e.target.value ? [e.target.value] : [] } }))}
                  className="h-8 max-w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`req-policy-${r.id}`}>
                  <option value="">Policy: none</option>
                  {policies.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
                <select value={draft.training_topic_ids?.[0] ?? (r.approved_training_topic_ids?.[0] || r.suggested_training_topic_ids?.[0] || "")}
                  onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...draft, training_topic_ids: e.target.value ? [e.target.value] : [] } }))}
                  className="h-8 max-w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`req-training-${r.id}`}>
                  <option value="">Training: none</option>
                  {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
                <input placeholder="Notes" value={draft.notes ?? r.notes ?? ""} onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...draft, notes: e.target.value } }))}
                  className="h-8 min-w-40 flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`req-notes-${r.id}`} />
                {dirty && (
                  <Button size="sm" onClick={() => save(r.id)} disabled={busy === r.id} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`req-save-${r.id}`}>
                    {busy === r.id ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {total > 0 && (
        <div className="flex items-center justify-between border-t border-stone-200 px-4 py-3" data-testid="req-pagination">
          <p className="text-xs text-stone-500" data-testid="req-pagination-info">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="border-stone-300" data-testid="req-page-prev">Previous</Button>
            <Button size="sm" variant="outline" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} className="border-stone-300" data-testid="req-page-next">Next</Button>
          </div>
        </div>
      )}

      {recs && (
        <div className="fixed inset-0 z-50 flex justify-center bg-black/40 p-6" data-testid="recs-modal">
          <div className="h-full w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 flex items-start justify-between border-b border-stone-200 bg-white p-5">
              <div>
                <p className="font-heading text-xl font-semibold text-stone-900">Review AI policy-home recommendations</p>
                <p className="mt-1 text-xs text-stone-500">Checked rows get assigned to the recommended policy when you Apply. &ldquo;New policy&rdquo; rows create drafts one at a time. Applying only links requirements — it never changes policy text.</p>
              </div>
              <button onClick={() => setRecs(null)} className="text-stone-400 hover:text-stone-700" data-testid="recs-close">✕</button>
            </div>
            <div className="divide-y divide-stone-100 px-4">
              {recs.length === 0 && <p className="py-8 text-center text-sm text-stone-400">No recommendations yet — run &ldquo;Recommend homes (AI)&rdquo; first.</p>}
              {recs.map((r) => (
                <div key={r.id} className="flex items-start gap-2 py-2" data-testid={`rec-row-${r.id}`}>
                  {r.recommended_policy_id ? (
                    <input type="checkbox" className="mt-1" checked={!!recSel[r.id]} onChange={() => setRecSel((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`rec-check-${r.id}`} />
                  ) : <span className="mt-1 w-3.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-stone-700">{r.text}</p>
                    <p className="mt-0.5 text-[11px] text-stone-400">
                      {r.recommended_policy_id
                        ? <>→ {(policies.find((x) => x.id === r.recommended_policy_id) || {}).title || "policy"}{r.recommendation_rationale ? ` — ${r.recommendation_rationale}` : ""}</>
                        : <>New policy needed: <strong>{r.recommended_new_policy}</strong>{r.recommendation_rationale ? ` — ${r.recommendation_rationale}` : ""} <button onClick={() => createPolicyFor(r).then(() => openRecs())} className="ml-1 text-accent-foreground underline" data-testid={`rec-create-${r.id}`}>Create draft</button></>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-stone-200 bg-white p-4">
              <Button variant="outline" onClick={() => setRecs(null)} className="border-stone-300" data-testid="recs-cancel">Close</Button>
              <Button onClick={applySelectedRecs} disabled={busy === "recs-apply"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="recs-apply-btn">
                {busy === "recs-apply" ? "Applying…" : `Apply ${Object.values(recSel).filter(Boolean).length} selected`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function AskTab({ sources }) {
  const [sessionId, setSessionId] = useState(() => `kb-${Date.now().toString(36)}`);
  const [sourceId, setSourceId] = useState("");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);

  const ask = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/knowledge/ask", { question: q, source_id: sourceId, session_id: sessionId }, { timeout: 120000 });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, citations: data.citations || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Error: ${formatApiError(e)}`, citations: [] }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, busy]);

  const scoped = sources.find((s) => s.id === sourceId);

  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="ask-panel">
      <div className="border-b border-stone-200 p-4">
        <h2 className="font-heading text-xl font-semibold text-stone-900">Ask the knowledge base</h2>
        <StepNote testid="stepnote-ask">
          Ask questions in plain English — answers come only from your source documents, with section/page citations. You can question unapproved sources while reviewing (marked not-yet-official). Great for &ldquo;does this chapter even apply to a non-medical agency?&rdquo;
        </StepNote>
        <p className="mt-1 max-w-3xl text-xs text-stone-500">
          Ask questions and get answers strictly from your source documents, with section/page citations. Scope to one document while you review it (e.g., &ldquo;does this chapter apply to a non-medical agency?&rdquo;) or search across everything.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className="h-9 max-w-96 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="ask-source-select">
            <option value="">All sources</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.title}{s.review_status !== "approved" ? " (not yet approved)" : ""}</option>)}
          </select>
          <button onClick={() => { setMessages([]); setSessionId(`kb-${Date.now().toString(36)}`); }}
            className="text-xs text-stone-500 hover:text-primary" data-testid="ask-new-session">New conversation</button>
        </div>
        {scoped && scoped.review_status !== "approved" && (
          <p className="mt-2 text-xs text-[#8a5d14]" data-testid="ask-unapproved-note">Reviewing an unapproved source — answers are from the extracted text only; nothing here is operational policy until you approve it.</p>
        )}
      </div>
      <div ref={threadRef} className="max-h-[420px] space-y-3 overflow-y-auto p-4" data-testid="ask-thread">
        {messages.length === 0 && (
          <p className="py-8 text-center text-sm text-stone-400" data-testid="ask-empty">
            Try: &ldquo;Does this chapter apply to a non-medical home care agency?&rdquo; or &ldquo;What does it require for annual training documentation?&rdquo;
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`max-w-3xl rounded-lg border p-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto border-primary/30 bg-accent text-stone-800" : "border-stone-200 bg-stone-50 text-stone-800"}`} data-testid={`ask-message-${i}`}>
            <p className="whitespace-pre-wrap">{m.content}</p>
            {m.citations?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`ask-citations-${i}`}>
                {m.citations.map((c, j) => (
                  <span key={j} className="rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[10px] text-stone-500" data-testid={`ask-citation-${i}-${j}`}>
                    [{j + 1}] {c.source_title}{c.section_title ? ` · ${c.section_title}` : ""}{c.page_start ? ` · p.${c.page_start}` : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}
      </div>
      <div className="flex gap-2 border-t border-stone-200 p-4">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder={scoped ? `Ask about ${scoped.title}…` : "Ask across all sources…"}
          className="h-10 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="ask-input" />
        <Button onClick={ask} disabled={busy || !input.trim()} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="ask-send">
          {busy ? "Thinking…" : "Ask"}
        </Button>
      </div>
    </section>
  );
}
